<script setup lang="ts">
import { scopeWpHtml } from '@/utils/scopeWpHtml';
import { executeWpScripts } from '@/utils/executeWpScripts';

const { blogUrlWpApi } = useLinks();

const isLoading = ref<boolean>(false);
const isLoadingCategories = ref<boolean>(false);
const wpPosts = ref<any>([]);
const wpCategories = ref<any>([]);
const selectedCategory = ref<number | null>(null);

const route = useRoute('blog-post');

const postSlug = computed(() => {
  return route.params.post;
});

const singleItem = computed(() => {
  const filter = wpPosts.value.filter((item: any) => {
    return item.slug === postSlug.value;
  });

  return filter[0] ?? null;
});

const filteredPosts = computed(() => {
  if (!selectedCategory.value) {
    return wpPosts.value;
  }

  return wpPosts.value.filter(
    (post: any) =>
      post.categories && post.categories.includes(selectedCategory.value),
  );
});

const featuredPost = computed(() => {
  if (!wpPosts.value?.length || !wpCategories.value?.length) return null;

  const destaquesCategory = wpCategories.value.find(
    (cat: any) => cat.name?.toLowerCase().trim() === 'destaques',
  );

  if (!destaquesCategory) return null;

  return (
    wpPosts.value.find(
      (post: any) =>
        post.categories && post.categories.includes(destaquesCategory.id),
    ) || null
  );
});

const isBannerSpecial = computed(() => featuredPost.value !== null);

const regularPosts = computed(() => {
  if (!selectedCategory.value) {
    const destaquesCategory = wpCategories.value.find(
      (cat: any) => cat.name?.toLowerCase().trim() === 'destaques',
    );

    if (!destaquesCategory) return wpPosts.value;

    const destaquesPosts = wpPosts.value.filter(
      (post: any) =>
        post.categories && post.categories.includes(destaquesCategory.id),
    );

    const otherPosts = wpPosts.value.filter(
      (post: any) =>
        !post.categories || !post.categories.includes(destaquesCategory.id),
    );

    return [...destaquesPosts, ...otherPosts];
  }

  return filteredPosts.value;
});

const categoriesWithCount = computed(() => {
  return wpCategories.value
    .map((cat: any) => {
      const count = wpPosts.value.filter(
        (post: any) => post.categories && post.categories.includes(cat.id),
      ).length;

      return { ...cat, count };
    })
    .filter((cat: any) => cat.count > 0);
});

async function getCategories() {
  if (!blogUrlWpApi.value) return;

  const categoriesUrl = blogUrlWpApi.value.replace('/posts', '/categories');

  isLoadingCategories.value = true;

  const { ApiService } = useApiService();

  await ApiService.get_external(categoriesUrl)
    .then((res: any) => {
      wpCategories.value = res.data;
      isLoadingCategories.value = false;
    })
    .catch(() => {
      isLoadingCategories.value = false;
    });
}

async function getPosts() {
  if (!blogUrlWpApi.value) return;

  isLoading.value = true;

  const { ApiService } = useApiService();

  await ApiService.get_external(blogUrlWpApi.value)
    .then((res: any) => {
      wpPosts.value = res.data;
      isLoading.value = false;
    })
    .catch(() => {
      isLoading.value = false;
    });
}

function selectCategory(categoryId: number | null) {
  selectedCategory.value = categoryId;
}

function sanitizeWpHtml(html: string): string {
  if (!html) return '';

  return html.replace(/\s*style="[^"]*"/gi, '');
}

function sanitizeTitle(title: string): string {
  if (!title) return '';

  const h1Match = title.match(/<h1[^>]*>(.*?)<\/h1>/i);

  if (h1Match && h1Match[1]) {
    let cleanTitle = h1Match[1];

    cleanTitle = cleanTitle.replace(/<span[^>]*>.*?<\/span>/gi, '');
    cleanTitle = cleanTitle.replace(/<[^>]+>/g, '');
    cleanTitle = cleanTitle.replace(/&#038;/g, '&');
    cleanTitle = cleanTitle.replace(/&amp;/g, '&');
    cleanTitle = cleanTitle.replace(/\s+/g, ' ');

    return cleanTitle.trim();
  }

  let cleanTitle = sanitizeWpHtml(title) || '';

  cleanTitle = cleanTitle.replace(
    /ATIVO\s+Atualizado em\s+[A-Z]{3}\s+\d{1,2},\s*\d{4},\s*\d{1,2}:\d{2}/gi,
    '',
  );

  return cleanTitle.trim();
}

function getCleanExcerpt(post: any): string {
  let text = '';

  if (post?.excerpt?.rendered) {
    text = post.excerpt.rendered;
  } else if (post?.yoast_head_json?.og_description) {
    return post.yoast_head_json.og_description;
  } else if (post?.content?.rendered) {
    text = post.content.rendered;
    text = text.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
    text = text.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
  } else {
    return '';
  }

  text = text.replace(/<[^>]+>/g, '');
  text = text.replace(/&nbsp;/g, ' ');
  text = text.replace(/&hellip;/g, '...');
  text = text.replace(/&#\d+;/g, '');
  text = text.replace(/&[a-z]+;/gi, '');
  text = text.replace(/\s+/g, ' ').trim();

  const exclamationMatch = text.match(/^[^!?]+[!?]/);

  if (exclamationMatch) {
    return exclamationMatch[0].trim();
  }

  return text.substring(0, 150).trim();
}

const contentRef = ref<HTMLElement | null>(null);
let cleanupWpScripts: (() => void) | null = null;

watch(
  () => singleItem.value?.content?.rendered,
  async (content) => {
    if (!content) return;
    cleanupWpScripts?.();
    await nextTick();
    if (contentRef.value) {
      cleanupWpScripts = executeWpScripts(contentRef.value);
    }
  },
);

onUnmounted(() => {
  cleanupWpScripts?.();
});

onMounted(() => {
  getPosts();
  getCategories();
});
</script>

<template>
  <div class="blog-wrapper">
    <section class="blog-container">
      <header
        class="blog-header"
        :draggable="false"
      >
        <div class="flex justify-between items-between w-full h-[36px]">
          <div class="flex items-center">
            <button
              aria-label="Voltar"
              class="blog-back-button"
              @click="$router.back()"
            >
              <NuxtIcon name="arrows/arrow_left" />
            </button>
            <span class="blog-header-title">
              <h1>
                {{ postSlug ? $t('casino.game.go_back') : $t('sidebar.blog') }}
              </h1>
            </span>
          </div>
          <div
            v-if="!postSlug"
            class="flex items-center"
          >
            <button
              aria-label="Atualizar"
              class="blog-action-button"
              @click="getPosts"
            >
              <NuxtIcon
                :class="['blog-sync-icon', { 'blog-animate': isLoading }]"
                name="sync"
              />
            </button>
          </div>
        </div>
      </header>

      <div
        v-if="!postSlug && !isLoadingCategories"
        class="blog-tabs"
      >
        <button
          :class="['tab', { active: selectedCategory === null }]"
          @click="selectCategory(null)"
        >
          <span class="tab-label">Todas</span>
        </button>
        <button
          v-for="category in categoriesWithCount"
          :key="category.id"
          :class="['tab', { active: selectedCategory === category.id }]"
          @click="selectCategory(category.id)"
        >
          <span class="tab-label">{{ category.name }}</span>
          <div class="tab-badge">
            <span class="tab-count">{{ category.count }}</span>
          </div>
        </button>
      </div>

      <section
        v-if="postSlug"
        class="blog-view blog-content-sandbox"
      >
        <div
          v-if="isLoading"
          class="loading"
        >
          <div
            class="load skeleton-loading"
            style="height: 400px"
          ></div>
          <div
            v-for="(_n, index) in 30"
            :key="`skeleton-${index}`"
            class="load skeleton-loading"
            style="height: 20px; margin: 15px 0"
          ></div>
        </div>

        <div
          v-if="
            singleItem?.yoast_head_json?.og_image &&
            singleItem?.yoast_head_json?.og_image.length &&
            singleItem?.title?.rendered
          "
          class="thumb wp-thumb"
        >
          <img
            :alt="singleItem?.title?.rendered"
            :src="singleItem?.yoast_head_json?.og_image[0]?.url"
          />
        </div>

        <div
          v-if="singleItem?.title?.rendered"
          class="title wp-title"
        >
          <div v-html="singleItem?.title?.rendered"></div>
        </div>

        <div
          v-if="singleItem?.content?.rendered"
          ref="contentRef"
          class="cactus-blog content wp-content"
        >
          <div
            class="blog-content-sandbox"
            v-html="
              scopeWpHtml(singleItem.content.rendered, '.blog-content-sandbox')
            "
          ></div>
        </div>

        <div
          v-if="!singleItem && !isLoading"
          class="alert alert-warning"
        >
          <span>{{ $t('no_posts_found') }}</span>
        </div>
      </section>

      <section
        v-else-if="wpPosts && wpPosts.length"
        class="blog-section"
      >
        <nuxt-link
          v-if="!selectedCategory && featuredPost"
          :key="featuredPost?.id"
          class="featured-banner hidden md:flex"
          :to="`/blog/${featuredPost.slug}`"
        >
          <div class="featured-content">
            <div
              v-if="
                featuredPost?.yoast_head_json?.og_image &&
                featuredPost?.yoast_head_json?.og_image.length &&
                featuredPost?.title?.rendered
              "
              class="featured-image-container"
              style="outline: 1px solid #ff450c"
            >
              <div
                v-if="
                  !selectedCategory &&
                  featuredPost?.categories &&
                  featuredPost?.categories.length > 0
                "
                class="featured-category-tag"
                style="background: #ff450c"
              >
                <div
                  class="category-label"
                  style="color: #ffffff"
                >
                  {{
                    wpCategories.find(
                      (cat: any) => cat.id === featuredPost.categories[0],
                    )?.name || 'GERAL'
                  }}
                </div>
              </div>
              <img
                :alt="featuredPost?.title?.rendered"
                class="featured-image"
                :src="featuredPost?.yoast_head_json?.og_image[0]?.url"
              />
            </div>

            <div class="featured-info">
              <div class="featured-text">
                <div
                  v-if="featuredPost?.title?.rendered"
                  class="featured-title"
                >
                  <div
                    v-html="sanitizeTitle(featuredPost?.title?.rendered)"
                  ></div>
                </div>

                <div class="featured-excerpt">
                  {{ getCleanExcerpt(featuredPost) }}
                </div>
              </div>

              <div class="featured-footer">
                <div class="featured-date">
                  <NuxtIcon
                    class="featured-date-icon"
                    name="blog/calendar"
                  />
                  <div class="featured-date-text">
                    {{
                      new Date(featuredPost?.date).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                      })
                    }}
                  </div>
                </div>
                <div class="featured-read-more">
                  <div class="featured-read-more-text">LER MAIS</div>
                  <NuxtIcon
                    class="featured-read-more-icon"
                    name="mission/button/participar"
                  />
                </div>
              </div>
            </div>
          </div>
        </nuxt-link>

        <nuxt-link
          v-for="(post, index) in regularPosts"
          :key="post?.id"
          :class="[
            'post-card',
            {
              'md:hidden': !selectedCategory && index === 0 && isBannerSpecial,
            },
          ]"
          :to="`/blog/${post.slug}`"
        >
          <div class="post-content">
            <div
              class="post-image-wrapper"
              style="border: 1px solid #ff450c"
            >
              <div
                v-if="
                  post?.yoast_head_json?.og_image &&
                  post?.yoast_head_json?.og_image.length &&
                  post?.title?.rendered
                "
                class="post-image"
              >
                <div
                  v-if="
                    !selectedCategory &&
                    post?.categories &&
                    post?.categories.length > 0
                  "
                  class="post-category-tag"
                  style="background: #ff450c"
                >
                  <div
                    class="category-label"
                    style="color: #ffffff"
                  >
                    {{
                      wpCategories.find(
                        (cat: any) => cat.id === post.categories[0],
                      )?.name || 'GERAL'
                    }}
                  </div>
                </div>
                <img
                  :alt="post?.title?.rendered"
                  :src="post?.yoast_head_json?.og_image[0]?.url"
                />
              </div>
            </div>

            <div class="post-info">
              <div class="post-text">
                <div
                  v-if="post?.title?.rendered"
                  class="post-title"
                >
                  <div v-html="sanitizeTitle(post?.title?.rendered)"></div>
                </div>

                <div class="post-excerpt">
                  {{ getCleanExcerpt(post) }}
                </div>
              </div>
            </div>
          </div>

          <div class="md:hidden post-footer-mobile">
            <div class="post-date">
              <NuxtIcon
                class="date-icon"
                name="blog/calendar"
              />
              <div class="date-text">
                {{
                  new Date(post?.date).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })
                }}
              </div>
            </div>
            <div class="post-read-more">
              <div class="read-more-text">LER MAIS</div>
              <NuxtIcon
                class="read-more-icon"
                name="mission/button/participar"
              />
            </div>
          </div>

          <div class="hidden md:flex post-footer">
            <div class="post-date">
              <NuxtIcon
                class="date-icon"
                name="blog/calendar"
              />
              <div class="date-text">
                {{
                  new Date(post?.date).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })
                }}
              </div>
            </div>
            <div class="post-read-more">
              <div class="read-more-text">LER MAIS</div>
              <NuxtIcon
                class="read-more-icon"
                name="mission/button/participar"
              />
            </div>
          </div>
        </nuxt-link>
      </section>
      <section
        v-else-if="isLoading"
        class="blog-section loading"
      >
        <div
          v-for="(_n, index) in 10"
          :key="`skeleton-${index}`"
          class="post skeleton-loading"
          style="height: 280px"
        ></div>
      </section>
      <section
        v-else
        class="blog-section"
      >
        <div class="alert alert-warning">
          <span>{{ $t('no_posts_found') }}</span>
        </div>
      </section>
    </section>
  </div>
</template>

<style scoped lang="scss">
.blog-view {
  @apply flex justify-between items-stretch flex-wrap gap-[15px] md:gap-[25px] text-left;
  .loading {
    @apply w-full;
    .load {
      @apply flex-1 min-w-[90%] max-w-[100%] rounded overflow-hidden;
    }
  }
  .thumb {
    @apply w-full overflow-hidden relative object-cover object-center bg-bg-secondary flex items-center justify-center rounded;
    img {
      @apply min-w-full min-h-full max-h-full w-auto h-auto object-cover object-center;
    }
  }
  .title {
    /* no mobile estava caindo para `text-base` (~16px). Aqui forçamos 24px sempre */
    @apply text-primary text-2xl font-semibold -mt-2;
    font-size: 24px;
    line-height: 1.3;

    /* o v-html pode renderizar h1/h2/etc sem o atributo do scoped, então usamos :deep */
    :deep(*) {
      font-size: 24px !important;
      line-height: 1.3 !important;
      margin: 0 !important;
    }
  }
  .content {
    @apply w-full overflow-hidden;
    img {
      @apply max-w-full;
    }
  }
}

.blog-section {
  @apply flex justify-start items-stretch flex-wrap gap-[15px] md:gap-[25px];

  .featured-banner {
    @apply w-full rounded-[10px] overflow-hidden cursor-pointer no-underline;
    background-color: rgb(3, 7, 18);
    outline: 1px solid rgb(38, 38, 38);
    outline-offset: -1px;
    transition: opacity 0.2s;
    flex-direction: column;

    &:hover {
      @apply opacity-90;
    }

    .featured-content {
      @apply p-2.5 md:p-5 flex flex-col gap-5;

      .featured-image-container {
        @apply w-full rounded-[20px] relative flex flex-col justify-start items-start;
        outline-offset: -1px;
        overflow: hidden;
        aspect-ratio: 1240 / 541;
        max-height: 500px;

        .featured-category-tag {
          @apply px-3.5 py-2 rounded-br-[10px] inline-flex justify-center items-center gap-[5px];
          @apply absolute;
          top: -1px;
          left: -1px;
          z-index: 20;
          border-top-left-radius: 20px;
          box-shadow: 0 0 0 1px #ff450c;

          .category-label {
            @apply text-white text-sm font-extrabold leading-[9px];
            font-family: 'Plus Jakarta Sans', sans-serif;
          }
        }

        .featured-image {
          @apply w-full h-full object-cover;
        }
      }

      .featured-info {
        @apply flex justify-between items-center gap-5;

        .featured-text {
          @apply flex-1 flex flex-col justify-center items-start gap-2.5;

          .featured-title {
            @apply w-full text-white font-bold;
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-size: 30px !important;
            line-height: 1.25 !important;

            /* Limita a 2 linhas com reticências */
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            padding-bottom: 2px;

            :deep(:where(*)) {
              font-size: inherit !important;
              line-height: inherit !important;
              font-weight: 700 !important;
              margin: 0 !important;
            }
          }

          .featured-excerpt {
            @apply w-full text-white/50 font-medium;
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-size: 14px;
            line-height: 1.4;

            /* Limita a 2 linhas com reticências */
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;

            :deep(p) {
              margin: 0 !important;
              display: inline;
            }
          }
        }

        .featured-footer {
          @apply pl-2.5 md:pl-5 pr-2.5 py-2.5 bg-[#1a1d29] rounded-[10px] flex justify-between items-center gap-4;
          flex-shrink: 0;
          min-width: fit-content;

          .featured-date {
            @apply px-[5px] flex justify-start items-center gap-2;

            .featured-date-icon {
              @apply text-white/50;
              font-size: 16px;
            }

            .featured-date-text {
              @apply text-white/50 text-xs font-bold leading-4;
              font-family: 'Plus Jakarta Sans', sans-serif;
              white-space: nowrap;
            }
          }

          .featured-read-more {
            @apply h-10 px-5 rounded-md flex justify-center items-center gap-1.5;
            background: radial-gradient(
              ellipse 138% 168.3% at 36.76% -16.67%,
              #ff704a 0%,
              #ff3400 100%
            );
            outline: 0.5px solid rgba(255, 195, 195, 0.3);
            outline-offset: -0.5px;

            .featured-read-more-text {
              @apply text-white text-sm font-extrabold leading-3;
              font-family: 'Plus Jakarta Sans', sans-serif;
            }

            .featured-read-more-icon {
              @apply text-white;
              font-size: 16px;
            }
          }
        }
      }
    }
  }

  .post-card {
    @apply p-2.5 md:p-5 rounded-[10px] backdrop-blur-[10px] overflow-hidden cursor-pointer no-underline;
    @apply w-full md:w-[calc(50%-12.5px)];
    background-color: rgb(3, 7, 18);
    outline: 1px solid rgb(38, 38, 38);
    outline-offset: -1px;
    transition: opacity 0.2s;
    display: flex;
    flex-direction: column;

    &:hover {
      @apply opacity-90;
    }

    .post-content {
      @apply flex flex-col justify-start items-start gap-5;
      flex: 1;

      .post-image-wrapper {
        @apply w-full rounded-[10px] flex flex-col justify-between items-center;
        overflow: hidden;

        .post-image {
          @apply w-full relative overflow-hidden rounded-[10px] bg-black;
          aspect-ratio: 1240 / 541;

          .post-category-tag {
            @apply px-3.5 py-2 rounded-br-[10px] inline-flex justify-center items-center gap-[5px];
            @apply absolute;
            top: -1px;
            left: -1px;
            z-index: 20;
            border-top-left-radius: 10px;
            box-shadow: 0 0 0 1px #ff450c;

            .category-label {
              @apply text-white text-xs font-extrabold leading-[9px];
              font-family: 'Plus Jakarta Sans', sans-serif;
            }
          }

          img {
            @apply w-full h-full object-contain object-center rounded-[10px] block;
          }
        }
      }

      .post-info {
        @apply w-full flex flex-col justify-start items-start gap-0;

        .post-text {
          @apply w-full px-0 md:px-2.5 pb-0 flex flex-col justify-center items-center gap-2.5;

          .post-title {
            @apply w-full text-white font-bold;
            font-family: 'Plus Jakarta Sans', sans-serif;

            /* garante 24px mesmo se vier h1/h2/strong/a no HTML do WP */
            font-size: 24px !important;
            line-height: 1.2 !important;

            /* v-html injeta elementos sem o atributo do scoped; :deep garante o override */
            :deep(:where(*)) {
              font-size: 24px !important;
              line-height: 1.2 !important;
              font-weight: 700 !important;
              margin: 0 !important;
            }

            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .post-excerpt {
            @apply w-full text-white/50 font-medium;
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-size: 14px;
            line-height: 1.3;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
    }

    // MOBILE: footer unificado (data e botão lado a lado)
    .post-footer-mobile {
      @apply w-full pl-2.5 pr-2.5 bg-white/5 rounded-[10px] flex justify-between items-center;
      padding-top: 6px;
      padding-bottom: 6px;
      margin-top: 12px;

      .post-date {
        @apply px-[5px] flex justify-start items-center gap-1.5;
        width: auto;
        flex: 1;

        .date-icon {
          @apply text-white/50;
          font-size: 14px;
        }

        .date-text {
          @apply text-white/50 font-bold;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 11px;
          line-height: 1.2;
        }
      }

      .post-read-more {
        @apply px-2.5 rounded-md flex justify-center items-center gap-1.5;
        height: 32px;
        width: auto;
        background: radial-gradient(
          ellipse 138% 168.3% at 36.76% -16.67%,
          #ff704a 0%,
          #ff3400 100%
        );
        outline: 0.5px solid rgba(255, 195, 195, 0.3);
        outline-offset: -0.5px;

        .read-more-text {
          @apply text-white font-extrabold;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 12px;
          line-height: 1.2;
        }

        .read-more-icon {
          @apply text-white;
          font-size: 14px;
        }
      }
    }

    // wrapper .post-footer: usado no desktop
    .post-footer {
      @apply w-full pl-2.5 md:pl-5 pr-2.5 py-2.5 bg-white/5 rounded-[10px] flex justify-between items-center;
      margin-top: 12px;

      @media (min-width: 768px) {
        margin-top: 20px;
      }

      .post-date {
        @apply px-[5px] md:px-[5px] flex justify-start items-center gap-2;
        width: auto;
        flex: 1;
        margin-top: 0;
        padding: 5px;
        background: transparent;
        border-radius: 0;

        .date-icon {
          @apply text-white/50;
          font-size: 16px;
        }

        .date-text {
          @apply text-white/50 text-xs font-bold leading-4;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }
      }

      .post-read-more {
        @apply h-10 px-2.5 md:px-5 rounded-md flex justify-center items-center gap-2;
        width: auto;
        margin-top: 0;
        background: radial-gradient(
          ellipse 138% 168.3% at 36.76% -16.67%,
          #ff704a 0%,
          #ff3400 100%
        );
        outline: 0.5px solid rgba(255, 195, 195, 0.3);
        outline-offset: -0.5px;

        .read-more-text {
          @apply text-white text-sm font-extrabold leading-3;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }

        .read-more-icon {
          @apply text-white;
          font-size: 16px;
        }
      }
    }
  }
}

.blog-wrapper {
  @apply w-full -mt-4 lg:-mt-5;
}

.blog-container {
  @apply mt-0 md:mt-0 mb-6 md:mb-10;
}

// Header do Blog (estilo Loja VIP)
.blog-header {
  @apply mt-2 mb-2 lg:flex items-center lg:justify-start;
}

.blog-back-button {
  @apply flex items-center bg-transparent text-texts/60;

  svg {
    @apply flex-none;
  }
}

.blog-header-title {
  @apply inline ml-3 text-[15px] text-texts font-bold select-none;
}

.blog-action-button {
  @apply p-2 bg-transparent text-white/60 hover:text-white transition-colors cursor-pointer flex items-center justify-center;

  svg {
    @apply w-5 h-5;
  }

  @media (max-width: 767px) {
    @apply bg-[#010612] rounded-md px-2 py-1.5 h-auto min-h-[32px];
    outline: 0.59px solid rgba(38, 38, 38, 1);
    outline-offset: -0.59px;
    backdrop-filter: blur(5.85px);
  }
}

.blog-sync-icon {
  svg {
    @apply w-4 h-4;
  }

  @media (max-width: 767px) {
    svg {
      @apply w-3.5 h-3.5;
    }
  }
}

.blog-animate {
  animation: rotate 600ms infinite linear;
}

@keyframes rotate {
  to {
    transform: rotate(1turn);
  }
}

.blog-tabs {
  @apply flex justify-center items-center mb-4 border-b border-[#424344];
  gap: 7.5px;
  overflow-x: auto;
  flex-wrap: nowrap;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;

  /* No desktop, alinha à esquerda */
  @media (min-width: 1024px) {
    @apply justify-start;
  }

  /* No mobile, permite scroll horizontal */
  @media (max-width: 639px) {
    @apply justify-start;
    -webkit-overflow-scrolling: touch;
  }

  .tab {
    @apply px-[15px] flex-shrink-0 rounded-[10px] font-semibold border transition-colors duration-150 cursor-pointer;
    @apply flex items-center gap-[5px];
    height: 31px;
    background: #09112c;
    color: #fff;
    outline: none;
    box-shadow: none;
    font-size: 12.507px;
    border: 0.5px solid #30364d;
    font-weight: 300;

    &:hover {
      @apply opacity-80;
    }

    .tab-label {
      @apply text-white;
      white-space: nowrap;
    }

    &.active {
      color: #00c9ff;
      border: 1px solid #00c9ff;
      background: linear-gradient(90deg, #00c9ff00 0%, #00c9ff33 100%);
      font-weight: 700;

      .tab-label {
        color: #00c9ff;
      }
    }

    .tab-badge {
      @apply w-4 h-4 rounded-full flex items-center justify-center;
      background-color: #ff450c;

      .tab-count {
        @apply text-white text-[9.62px] font-semibold leading-3;
        font-family: 'Plus Jakarta Sans', sans-serif;
      }
    }

    @media (max-width: 639px) {
      @apply px-[10px] py-[5px];
      min-width: 70px;
      white-space: nowrap;
      flex: 0 0 auto;
    }
  }
}
</style>

<style lang="scss">
.cactus-blog.content {
  @apply text-xs md:text-sm text-texts leading-7;
  a {
    &:hover {
      @apply text-primary underline;
    }
  }

  p {
    @apply leading-7 my-4 text-base;
  }

  img {
    @apply max-w-full h-auto inline-block;
  }

  video {
    @apply max-w-full h-auto inline-block;
  }

  h2 {
    @apply text-[2rem];
  }

  div:has(> figure > video) {
    @apply grid grid-cols-[50%,_1fr] grid-rows-[auto];
  }

  figcaption {
    @apply opacity-15;
  }

  ul {
    list-style: inside;

    li {
      @apply my-2;
    }
  }

  ol {
    list-style: decimal inside;

    li {
      @apply my-2;
    }
  }

  blockquote {
    @apply my-4 p-4 bg-bg-secondary rounded border-l-4 border-primary text-texts;
    p {
      @apply m-0;
    }
  }

  .section-title {
    @apply text-primary bg-auth-bg-primary rounded-3xl text-lg font-bold p-2 text-center uppercase mt-12;
  }

  .cta {
    @apply text-bg-primary bg-primary rounded-3xl text-lg font-bold p-4 text-center uppercase mt-12;
  }

  .wp-block-table {
    @apply mb-2.5 mt-1;
    table.has-background {
      border-spacing: 1px;
      border-collapse: separate;
      border: 1px solid white;
      background-color: white !important;

      th {
        @apply text-texts/90 bg-sidebar-bg p-2.5 w-dvw text-center;
      }
      tr td:first-child {
        @apply text-texts/100 bg-sidebar-bg p-2.5;
      }
      tr td:nth-child(2) {
        @apply text-texts/100 bg-sidebar-bg p-2.5;
        width: 80%;
      }
    }
  }
  .wp-block-group-is-layout-flex {
    @apply text-secondary flex flex-col items-center justify-center;
    max-height: 200px !important;
    min-height: 180px !important;
    .has-medium-font-size {
      @apply text-lg m-0 p-0;
    }
  }
  div.cta-container {
    margin-bottom: 10px;
  }
}
.wp-block-social-links {
  @apply flex items-center justify-center flex-nowrap list-none text-lg gap-4 my-4 mx-0 p-0;
  a[href='^http'] {
    @apply text-primary hover:opacity-90 no-underline;
  }
  svg {
    @apply h-[1.25em] w-[1.25em] text-current fill-current;
  }
}
.wp-block-button__link {
  @apply text-bg-primary bg-primary rounded-full shadow-none no-underline py-2 px-5 text-base inline-block text-center break-words cursor-pointer hover:text-bg-primary hover:opacity-80 hover:no-underline;
}
// FIX TO WP
.screen-reader-text {
  @apply overflow-hidden w-[1px] h-[1px];
  clip: rect(1px, 1px, 1px, 1px);
  position: absolute !important;
  word-wrap: normal !important;
}

/* Force post-title font size (card da listagem /blog)
   - Override TODOS os estilos inline que vêm do WordPress
   - Usa seletores específicos para cada tag possível
*/
.post-title,
.post-title * {
  font-size: 24px !important;
  line-height: 1.3 !important;
  font-weight: 700 !important;
  color: white !important;
  margin: 0 !important;
}

.post-title h1[style],
.post-title h2[style],
.post-title h3[style],
.post-title h4[style],
.post-title h5[style],
.post-title h6[style],
.post-title p[style],
.post-title span[style],
.post-title div[style],
.post-title a[style] {
  font-size: 24px !important;
  line-height: 1.3 !important;
  font-weight: 700 !important;
  color: white !important;
  margin: 0 !important;
  margin-bottom: 0 !important;
}

// Force h1 font size in blog content
.cactus-blog.content h1,
.cactus-blog.content h1[style] {
  font-size: 24px !important;
  line-height: 1.4 !important;
  font-weight: 700 !important;
}
</style>
